import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/marsheeApi";

const STATUSES = ["Active", "Incubating", "Back Burner", "Completed", "Abandoned"];
const ROLE_LABEL = { primary_driver: "Primary Strategic Driver", secondary_maintenance: "Secondary / Maintenance", none: "None" };
const eligible = (s) => s === "Active" || s === "Incubating";

function Label({ children }) { return <div className="mh-label mb-4">{children}</div>; }

function ProjectRow({ p, ventures = [], onChange, setFlash }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(p);
  useEffect(() => setDraft(p), [p]);

  const patch = async (body) => {
    try { await api.updateProject(p.id, body); await onChange(); setFlash("Saved."); }
    catch (e) { setFlash(e.message); }
  };
  const reassign = async (val) => {
    try { await api.updateProject(p.id, { venture_id: val || null }); await onChange(); setFlash(val ? "Moved to another venture." : "Set to Independent."); }
    catch (e) { setFlash(e.message); }
  };
  const status = async (s) => {
    try { await api.changeProjectStatus(p.id, s); await onChange(); setFlash(`Status set to ${s}.`); }
    catch (e) { setFlash(e.message); }
  };
  const makeDriver = async () => {
    try { await api.setPrimaryDriver(p.id); await onChange(); setFlash("Primary Strategic Driver updated."); }
    catch (e) { setFlash(e.message); }
  };
  const clearDriver = async () => {
    try { await api.unsetPrimaryDriver(); await onChange(); setFlash("Strategic driver cleared."); }
    catch (e) { setFlash(e.message); }
  };
  const isDriver = p.priority_role === "primary_driver";

  return (
    <li className="py-6" style={{ borderTop: "1px solid var(--mh-divider)" }} data-testid={`project-${p.id}`}>
      <div className="flex items-start justify-between gap-4">
        <button className="text-left" onClick={() => setOpen(!open)} data-testid={`project-toggle-${p.id}`}>
          <span className="mh-label" style={{ color: isDriver ? "var(--mh-accent)" : "var(--mh-muted)" }}>
            {p.status}{isDriver ? " · Primary Strategic Driver" : ""}
          </span>
          <p className="mh-serif mt-1" style={{ fontSize: "1.3rem", color: "var(--mh-text)" }}>{p.title}</p>
          {p.next_action && (<p className="mh-sans mt-1" style={{ fontSize: "0.9rem", color: "var(--mh-action)" }}>Next — {p.next_action}</p>)}
        </button>
      </div>

      {open && (
        <div className="mt-4 space-y-4" style={{ maxWidth: "48ch" }} data-testid={`project-editor-${p.id}`}>
          <Field label="Why it exists" value={draft.purpose} onSave={(v) => patch({ purpose: v })} />
          <Field label="Done means" value={draft.definition_of_done} onSave={(v) => patch({ definition_of_done: v })} />
          <Field label="Next action" value={draft.next_action} onSave={(v) => patch({ next_action: v })} />
          <Field label="Blocker (only if present)" value={draft.blocker} onSave={(v) => patch({ blocker: v || null })} />
          <div>
            <div className="mh-label">Status</div>
            <select className="mh-input mt-2" data-testid={`status-select-${p.id}`} value={p.status} onChange={(e) => status(e.target.value)}>
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <div className="mh-label">Venture</div>
            <select className="mh-input mt-2" data-testid={`venture-select-${p.id}`} value={p.venture_id || ""} onChange={(e) => reassign(e.target.value)}>
              <option value="">Independent</option>
              {ventures.map((v) => <option key={v.id} value={v.id}>{v.title}</option>)}
            </select>
          </div>
          <div>
            <div className="mh-label">Strategic role</div>
            {isDriver ? (
              <button className="mh-btn mh-btn-ghost mt-2" data-testid={`clear-driver-${p.id}`} onClick={clearDriver}>Remove as Primary Driver</button>
            ) : eligible(p.status) ? (
              <button className="mh-btn mh-btn-ghost mt-2" data-testid={`make-driver-${p.id}`} onClick={makeDriver}>Make Primary Strategic Driver</button>
            ) : (
              <p className="mh-sans mt-2" style={{ fontSize: "0.85rem", color: "var(--mh-muted)" }}>
                Only Active or Incubating projects can be the strategic driver.
              </p>
            )}
          </div>
        </div>
      )}
    </li>
  );
}

function Field({ label, value, onSave }) {
  const [v, setV] = useState(value || "");
  useEffect(() => setV(value || ""), [value]);
  return (
    <div>
      <div className="mh-label">{label}</div>
      <input className="mh-input mt-1" value={v} onChange={(e) => setV(e.target.value)}
        onBlur={() => { if ((v || "") !== (value || "")) onSave(v.trim()); }} placeholder="—" />
    </div>
  );
}

function CreateProject({ ventureId, onDone, setFlash }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try {
      const body = { title: title.trim() };
      if (ventureId) body.venture_id = ventureId;
      await api.createProject(body); setTitle(""); setOpen(false); await onDone(); setFlash("Project created.");
    } catch (err) { setFlash(err.message); }
  };
  if (!open) return <button className="mh-btn mh-btn-ghost mt-6" data-testid="open-create-project" onClick={() => setOpen(true)}>Define a project</button>;
  return (
    <form className="mt-6" onSubmit={submit} data-testid="create-project-form">
      <div className="mh-label">New project — title</div>
      <input className="mh-input mt-1" data-testid="project-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="A defined outcome" required style={{ maxWidth: "40ch" }} />
      <div className="mt-3 flex gap-3">
        <button type="submit" className="mh-btn mh-btn-primary" data-testid="submit-project" disabled={!title.trim()}>Create</button>
        <button type="button" className="mh-btn mh-btn-ghost" onClick={() => setOpen(false)}>Cancel</button>
      </div>
    </form>
  );
}

function VentureForm({ initial, onSaved, setFlash }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(initial || { title: "", description: "", status: "active", strategic_relevance_note: "" });
  const submit = async (e) => {
    e.preventDefault();
    try {
      if (initial) await api.updateVenture(initial.id, f); else await api.createVenture(f);
      setOpen(false); await onSaved(); setFlash(initial ? "Venture updated." : "Venture created.");
    } catch (err) { setFlash(err.message); }
  };
  if (!open) return (
    <button className={`mh-btn ${initial ? "mh-btn-ghost" : "mh-btn-primary"}`} data-testid={initial ? "edit-venture" : "open-create-venture"} onClick={() => setOpen(true)}>
      {initial ? "Edit venture" : "Add a venture"}
    </button>
  );
  return (
    <form className="mt-4 space-y-4" style={{ maxWidth: "46ch" }} onSubmit={submit} data-testid="venture-form">
      <div><div className="mh-label">Title</div><input className="mh-input mt-1" data-testid="venture-title-input" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} required /></div>
      <div><div className="mh-label">Description</div><input className="mh-input mt-1" value={f.description || ""} onChange={(e) => setF({ ...f, description: e.target.value })} /></div>
      <div><div className="mh-label">Status</div><input className="mh-input mt-1" value={f.status || ""} onChange={(e) => setF({ ...f, status: e.target.value })} /></div>
      <div className="flex gap-3">
        <button type="submit" className="mh-btn mh-btn-primary" data-testid="submit-venture" disabled={!f.title?.trim()}>Save</button>
        <button type="button" className="mh-btn mh-btn-ghost" onClick={() => setOpen(false)}>Cancel</button>
      </div>
    </form>
  );
}

export default function Ventures() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [flash, setFlash] = useState(null);
  const [ventures, setVentures] = useState([]);
  const [byVenture, setByVenture] = useState({});
  const [unassigned, setUnassigned] = useState([]);

  const load = useCallback(async () => {
    setError(null);
    try {
      const vs = await api.ventures();
      const map = {};
      await Promise.all(vs.map(async (v) => { map[v.id] = await api.projectsByVenture(v.id); }));
      setVentures(vs); setByVenture(map);
      setUnassigned(await api.unassignedProjects());
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="mx-auto max-w-4xl px-6 sm:px-12 py-16"><p className="mh-sans" style={{ color: "var(--mh-muted)" }}>Gathering what you are building…</p></div>;
  if (error && !ventures.length) return (
    <div className="mx-auto max-w-4xl px-6 sm:px-12 py-16" data-testid="ventures-error">
      <p className="mh-serif" style={{ fontSize: "1.3rem" }}>The archive is unreachable right now.</p>
      <p className="mh-sans mt-2" style={{ color: "var(--mh-muted)" }}>{error}</p>
      <button className="mh-btn mh-btn-ghost mt-6" onClick={load}>Try again</button>
    </div>
  );

  return (
    <main data-testid="ventures-page">
      {flash && (
        <div className="mx-auto max-w-4xl px-6 sm:px-12 pt-8">
          <div className="mh-fade" data-testid="ventures-flash" style={{ borderLeft: "2px solid var(--mh-action)", paddingLeft: "1rem", color: "var(--mh-muted)", fontSize: "0.95rem" }}>{flash}</div>
        </div>
      )}

      <div className="mx-auto max-w-4xl px-6 sm:px-12 pt-10 sm:pt-14 pb-8">
        <div className="flex items-baseline justify-between gap-4 flex-wrap">
          <Label>What You Are Building</Label>
          <VentureForm onSaved={load} setFlash={setFlash} />
        </div>
        {ventures.length === 0 && (
          <p className="mh-sans" data-testid="ventures-empty" style={{ color: "var(--mh-muted)", maxWidth: "44ch" }}>
            Nothing here yet. Name the larger things you are building when you are ready.
          </p>
        )}
      </div>

      {ventures.map((v) => (
        <div key={v.id} className="mh-band-taupe" data-testid={`venture-${v.id}`}>
          <div className="mx-auto max-w-4xl px-6 sm:px-12 py-10 sm:py-12">
            <div className="flex items-baseline justify-between gap-4 flex-wrap">
              <div>
                <span className="mh-label">{v.status}</span>
                <h2 className="mh-serif mt-1" style={{ fontSize: "1.9rem", color: "var(--mh-text)" }}>{v.title}</h2>
              </div>
              <VentureForm initial={v} onSaved={load} setFlash={setFlash} />
            </div>
            {v.description && <p className="mh-sans mt-3" style={{ color: "var(--mh-action)", maxWidth: "54ch" }}>{v.description}</p>}
            {(byVenture[v.id] || []).length === 0 ? (
              <p className="mh-sans mt-6" style={{ color: "var(--mh-muted)" }}>No projects defined for this venture yet.</p>
            ) : (
              <ul className="mt-6" style={{ borderBottom: "1px solid var(--mh-divider)" }}>
                {byVenture[v.id].map((p) => <ProjectRow key={p.id} p={p} ventures={ventures} onChange={load} setFlash={setFlash} />)}
              </ul>
            )}
            <CreateProject ventureId={v.id} onDone={load} setFlash={setFlash} />
          </div>
        </div>
      ))}

      <div className="mx-auto max-w-4xl px-6 sm:px-12 py-10 sm:py-14">
        <Label>Independent Projects</Label>
        {unassigned.length === 0 ? (
          <p className="mh-sans" data-testid="unassigned-empty" style={{ color: "var(--mh-muted)" }}>No projects stand on their own right now.</p>
        ) : (
          <ul style={{ borderBottom: "1px solid var(--mh-divider)" }} data-testid="unassigned-list">
            {unassigned.map((p) => <ProjectRow key={p.id} p={p} ventures={ventures} onChange={load} setFlash={setFlash} />)}
          </ul>
        )}
        <CreateProject ventureId={null} onDone={load} setFlash={setFlash} />
      </div>
    </main>
  );
}
